import React from "react";
import HelpTooltip from "../../components/HelpTooltip";

/**
 * Every explanation shown to a 3PL partner, in one file.
 *
 * WHY THE COPY LIVES HERE AND NOT BESIDE EACH FIELD
 * -------------------------------------------------
 * These sentences are the product's promises to a business partner. They decide
 * whether someone sets a cash limit correctly, whether they believe a penalty
 * will be charged, and whether they trust the acceptance rate we grade them on.
 * Scattered across eleven screens they cannot be reviewed as a whole, and they
 * drift from the code the moment a behaviour changes. In one file an operator
 * can read every claim the product makes in five minutes.
 *
 * THE RULE FOR WRITING ONE
 * ------------------------
 * Say what it does, then what it costs the partner if they get it wrong. Where
 * a field is recorded but drives nothing, SAY SO in the tooltip. A partner who
 * sets a late penalty believing it will be charged, and later learns it never
 * was, has been misled by us, and the fix is honest copy now rather than an
 * awkward conversation later. Anything marked `recorded` below is exactly that:
 * stored on the agreement, read by no code that moves money or picks a rider.
 */

export const PARTNER_HELP = Object.freeze({
  // ── Cash control ────────────────────────────────────────────────────────
  cashOnStreet: {
    title: "Cash on the street",
    body: "Lusuku money your riders are physically holding right now, collected on cash deliveries and not yet banked. It falls when a rider deposits.",
  },
  cashLimit: {
    title: "Held cash limit",
    body: "Once a rider is holding more than this, Lusuku stops offering them new orders until they deposit. It is a theft and loss control, so a higher limit means more cash exposed and fewer interruptions; a lower one means the opposite.",
  },
  fleetLimit: {
    title: "Fleet limit",
    body: "Applies to every rider on your fleet at once. A rider whose limit Lusuku set individually keeps that limit and is not changed by this.",
  },
  blockedRiders: {
    title: "Blocked riders",
    body: "Riders over their cash limit. They stay on your payroll and stay online, but receive no new orders until they bank what they are holding. This is usually the reason a rider has gone quiet.",
  },
  cashUtilisation: {
    title: "How close to the limit",
    body: "How much of a rider's cash limit is used. At 100 percent they stop receiving orders, so this is your early warning to send someone to bank.",
  },
  vehicleClass: {
    title: "Vehicle class",
    body: "Decides which orders a rider can be offered. An order needing a van is never offered to a motorcycle, so a van recorded as a motorcycle is a van that never gets the freight work it could carry.",
  },

  // ── Orders ──────────────────────────────────────────────────────────────
  offerWindow: {
    title: "Time to answer",
    body: "How long you have to accept an order a vendor has handed you. If nobody answers, the order goes back to the vendor automatically and counts against your acceptance rate exactly as a decline would.",
  },
  acceptOrder: {
    title: "Accepting",
    body: "Confirms you will deliver this order. If the drop off falls inside one of your service areas, accepting also starts the delivery guarantee for that area.",
  },
  declineOrder: {
    title: "Declining",
    body: "Sends the order straight back to the vendor to place elsewhere. It leaves your queue immediately. Declining is recorded and lowers your acceptance rate, but leaving an order unanswered costs you exactly the same and costs the customer the waiting time too.",
  },
  acceptanceRate: {
    title: "Acceptance rate",
    body: "The share of offers you accepted, out of every offer you were sent. Offers that timed out count as not accepted.",
  },

  // ── Service areas ───────────────────────────────────────────────────────
  serviceArea: {
    title: "Service areas",
    body: "The places you deliver. When a vendor picks a partner for a delivery, partners whose areas cover that address are listed first and the rest are grouped under a heading saying they do not cover it. A vendor can still choose you, so an area you have not drawn costs you visibility rather than blocking the work outright.",
  },
  serviceAreaRadius: {
    title: "Radius",
    body: "How far from the centre point this area reaches, as the crow flies. The circle on the map is exactly what the matcher uses.",
  },
  guaranteedMinutes: {
    title: "Delivery guarantee",
    body: "The delivery time you are promising in this area. When you accept an order dropping here, the clock starts and the promise is fixed at that moment, so a later edit to this area never changes a promise already made. Delivery is then measured against it.",
  },
  pricingTierRecorded: {
    title: "Price per delivery",
    body: "Recorded on your agreement for reference. It is not what Lusuku charges for a delivery today: the fee comes from the platform fee engine and your revenue share comes from your partnership tier.",
  },
  slaMoneyRecorded: {
    title: "Recorded, not charged",
    body: "Written down as part of the agreement for this area. No penalty is deducted and no bonus is paid automatically today. Treat these as the terms you have agreed, settled between you and Lusuku, not as something the platform moves money for.",
  },

  // ── Hubs ────────────────────────────────────────────────────────────────
  hubsRecorded: {
    title: "Hubs",
    body: "Your depots, recorded so Lusuku knows where you operate from and can reach you there. Nothing is routed through a hub automatically today: riders are matched from the vendor's location, not from a hub, and capacity and opening hours here do not gate dispatch.",
  },

  // ── Dispatch automation ─────────────────────────────────────────────────
  autoAssign: {
    title: "Auto-assign",
    body: "When on, Lusuku picks one of your riders for an accepted order instead of waiting for someone to do it by hand. Turning it off does not stop the orders, it means every one waits for a person.",
  },
  autoAssignStrategy: {
    title: "How a rider is chosen",
    body: "Proximity picks the nearest available rider. Performance favours your stronger riders. Round robin spreads work evenly across the fleet.",
  },
  autoAssignRadius: {
    title: "Search radius",
    body: "How far from the pickup point Lusuku will look for one of your riders. Too small and orders wait with nobody found; too large and a rider is sent further than the job is worth.",
  },
  autoAssignCooldown: {
    title: "Cooldown",
    body: "A short pause before the automatic choice is made, so a dispatcher who wants to take the job by hand still can.",
  },
  autoRejectTimeout: {
    title: "Rider response time",
    body: "How long a rider has to accept before the order moves on to the next rider.",
  },

  // ── Money ───────────────────────────────────────────────────────────────
  commissionSplit: {
    title: "How the delivery fee is split",
    body: "Every delivery fee is divided three ways between Lusuku, you and the rider. The percentages come from your partnership tier and always add up to the whole fee, so nothing is taken off the top.",
  },
  partnershipTier: {
    title: "Partnership tier",
    body: "Sets your share of every delivery fee. A higher tier means a larger share for you. Lusuku sets the tier; you cannot change it here.",
  },
  earningsVsPayouts: {
    title: "Earnings and payouts",
    body: "Earnings are what your completed deliveries have made. Payouts are what has actually been sent to your bank or mobile money. They differ until a payout run completes.",
  },
  payoutStatus: {
    title: "Payout status",
    body: "Pending means raised and waiting. Approved and processing mean it is on its way. Completed means the money has left Lusuku. Failed means the transfer was refused, usually because of the account details on your profile.",
  },

  // ── Account ─────────────────────────────────────────────────────────────
  staffPermissions: {
    title: "What your staff can reach",
    body: "Each area can be set to none, view or full. View lets someone see it, full lets them change it. These are enforced by the server, not just hidden from the menu, so a staff member cannot reach a screen you have closed to them.",
  },
  twoFactor: {
    title: "Two-step sign-in",
    body: "Adds a six digit code from an authenticator app to your sign-in, so a stolen password alone is not enough to reach your orders, riders and bank details.",
  },
});

/**
 * The tooltip for one topic. Naming a topic that does not exist renders
 * nothing rather than an empty bubble, so a typo is invisible to the partner
 * instead of being a confusing blank icon.
 */
const PartnerHelp = ({ topic, placement = "top" }) => {
  const entry = PARTNER_HELP[topic];
  if (!entry) return null;
  return <HelpTooltip title={entry.title} body={entry.body} placement={placement} />;
};

export default PartnerHelp;
